import type { DrawerProps } from "~/common/components/ui/Drawer";
import { Drawer } from "~/common/components/ui/Drawer";
import Spinner from "~/common/components/ui/Spinner";
import { extractFileNameFromPath } from "~/common/utils/extractFileNameFromPath";
import { useSignedConsentDocuments } from "~/providers/hooks/useSignedConsentDocuments";
import {
  formatDateToComplete,
  formatToMonthDayYear,
} from "~/providers/utils/formatDate";
import { ConsentAcknowledgedFeed } from "~/providers/utils/types/patientFeeds";

interface SignedConsentsDrawerProps extends DrawerProps {
  signedConsentsActivity?: ConsentAcknowledgedFeed;
}

export const SignedConsentsDrawer = ({
  signedConsentsActivity,
  ...props
}: SignedConsentsDrawerProps) => {
  const organizationId =
    signedConsentsActivity?.organization_patient?.organization?.id;

  const patientId = signedConsentsActivity?.organization_patient?.patient?.id;

  const { data: signedDocuments, isLoading } = useSignedConsentDocuments({
    organizationId,
    patientId,
  });

  return (
    <Drawer {...props}>
      {isLoading || !signedDocuments ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-scroll">
          {signedConsentsActivity && (
            <p className="mb-1 text-sm font-medium text-gray-30">
              Accepted on{" "}
              {formatToMonthDayYear(signedConsentsActivity.created_at)}
            </p>
          )}
          <h2 className="mb-1 text-xl font-semibold text-gray-80">
            Consent forms
          </h2>

          {signedDocuments.map((document, index) => (
            <div
              key={index}
              className="flex flex-col gap-3 border-b border-neutral-700 py-5 last:border-none"
            >
              <p className="font-medium text-gray-80">
                {document.consent_form_name}
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-4">
                <p className="text-gray-35">Link to file</p>
                <a
                  href={document.organization_consent_form_file.path}
                  className="font-medium text-gray-70 underline"
                >
                  {extractFileNameFromPath(
                    document.organization_consent_form_file.path,
                  )}
                </a>

                <p className="text-gray-35">Uploaded on</p>
                <p className="font-medium text-gray-70">
                  {formatDateToComplete(
                    document.organization_consent_form_file.created_at,
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};
